// 入會申請書專屬的顯示規則與費用/參數組裝純函式。
// 原則：無 React 依賴、不碰 ref、只接收原始值（數字、字串、布林）。
// 由 create 模組各元件先從 ref/state 取好原始值，再呼叫這裡的純函式。
import { cloneDeep } from 'lodash';
import { isEqual, mergeDeep } from '@util/utils';
import { isAfter, getFirstOfDay, getLastOfDay, parseDate } from '@util/moment';
import { PEOPLE_TYPE, PAYMENT_TYPE, MEMBERSHIP_TYPE, MEMBERSHIP_CONTRACT_VERSION } from '@icoach/constants/dataEnums';

// ── A 入口判斷 ─────────────────────────────────────────────
// 來源：MembershipCreatePage.jsx handleCreateClick 的前置檢查
// 是否要彈出「健康申告書」前置提示 dialog。
// isValid：健康申告書驗證結果（由元件端先 await 好再傳入）。
// 對應原式：(ONLYJOIN 且 id 為 0/'') || !isValid
export const shouldShowHealthDeclarationDialog = ({ type, id, isValid }) =>
    (isEqual(type, PEOPLE_TYPE.ONLYJOIN) && (id === 0 || id === '')) || !isValid;

// ── B 會員須知（notice）挑選 ─────────────────────────────────
// 來源：MembershipCreateContentSurvey.jsx checkMembershipType / makeNoticeData
// 依會籍類型與會員類型決定要顯示哪一組須知條款的 key。
// 對應原式：membershipType===2 → renewal；memberType===6 → newStudent；否則 new。
export const resolveNoticeKey = (membershipType, memberType) => {
    if (isEqual(membershipType, 2)) return 'renewal';
    if (isEqual(memberType, 6)) return 'newStudent';
    return 'new';
};

// ── C 初次費用與收款金額計算 ─────────────────────────────────
// 來源：MembershipCreateContentFee.jsx handleChangeFee / handleChangePaymentAmount / initCalculate
// 初次費用總額 = 入會費 + 首月費 + 隔月費（各自轉數字後相加）。
// 對應原式：sum(Object.values({ joinFee, firstMonthFee, nextMonthFee }))
export const computeFeeTotal = ({ joinFee, firstMonthFee, nextMonthFee }) =>
    Number(joinFee) + Number(firstMonthFee) + Number(nextMonthFee);

// 收款總額 = 信用卡金額 + 現金金額。
// 對應原式：sum(Object.values({ cardPaymentAmount, cashPaymentAmount }))
export const computePaymentTotal = ({ cardPaymentAmount, cashPaymentAmount }) =>
    Number(cardPaymentAmount) + Number(cashPaymentAmount);

// 初始收款金額：未勾選「即時開立發票」（isImmediatelyIssuing=false）時金額為 0，
// 否則取原金額（空值視為 0）。對應原式：isImmediatelyIssuing ? (amount || 0) : 0
export const resolveInitialPaymentAmount = (isImmediatelyIssuing, amount) =>
    isImmediatelyIssuing ? (amount || 0) : 0;

// ── D 付款方式顯示判斷 ───────────────────────────────────────
// 來源：MembershipCreateContentPayment.jsx getPaymentTypeText / L192 / L209
// 依付款方式數值取出對應顯示文字；找不到時回傳空字串。
// paymentTypeOptions：由元件端傳入的選項來源（原 import 的 PAYMENT_TYPE_OPTIONS）。
export const getPaymentTypeText = (paymentTypeOptions, num) =>
    paymentTypeOptions.find((item) => isEqual(item.value, num))?.text ?? '';

// 是否顯示「信用卡資訊」區塊（付款方式為信用卡自動扣款）。
export const shouldShowCreditCard = (paymentType) => isEqual(paymentType, PAYMENT_TYPE.CREDITCARD);

// 是否顯示「銀行資訊」區塊（付款方式為銀行自動扣款 ACH）。
export const shouldShowAch = (paymentType) => isEqual(paymentType, PAYMENT_TYPE.ACH);

// ── E 入會方案頁顯示判斷 ─────────────────────────────────────
// 來源：MembershipCreateContentPlan.jsx
// 「申請日期」是否鎖定（disabled）。
// 2017 版合約：對象為「移入會員」時鎖定；其餘版本：對象為「移轉會員」時鎖定。
// 對應原式（L100-103）：contractVersion===2017 ? targetType===TRANSFERINMEMBER : targetType===TRANSFERMEMBER
export const isApplyDateDisabled = ({ contractVersion, targetType }) =>
    isEqual(contractVersion, MEMBERSHIP_CONTRACT_VERSION.MEMBERSHIP2017)
        ? isEqual(targetType, PEOPLE_TYPE.TRANSFERINMEMBER)
        : isEqual(targetType, PEOPLE_TYPE.TRANSFERMEMBER);

// 是否顯示「轉約」相關欄位（原會員姓名／編號／使用原編號）。
// 對應原式（L393）：membershipType===TRANSFER
export const shouldShowTransferFields = (membershipType) => isEqual(membershipType, MEMBERSHIP_TYPE.TRANSFER);

// 是否顯示「復會來源」欄位（對象為停權會員且會籍類型為一般）。
// 對應原式（L439）：targetType===INVALIDMEMBER && membershipType===NORMAL
export const shouldShowRecoveryType = ({ targetType, membershipType }) =>
    isEqual(targetType, PEOPLE_TYPE.INVALIDMEMBER) && isEqual(membershipType, MEMBERSHIP_TYPE.NORMAL);

// 是否顯示「來源」選擇（僅入會對象）。
// 對應原式（L459）：targetType===ONLYJOIN
export const shouldShowOnlyJoinSource = (targetType) => isEqual(targetType, PEOPLE_TYPE.ONLYJOIN);

// 是否顯示「優惠折扣」區塊（非轉約時顯示）。
// 對應原式（L574）：!(membershipType===TRANSFER)
export const shouldShowDiscount = (membershipType) => !isEqual(membershipType, MEMBERSHIP_TYPE.TRANSFER);

// 是否顯示「請走轉約流程」提示（不可修改合約長度、可轉約、且對象為會員）。
// 對應原式（L520）：!canChangeMembershipTerm && canExchange && targetType===MEMBER
export const shouldShowExchangeHint = ({ canChangeMembershipTerm, canExchange, targetType }) =>
    !canChangeMembershipTerm && canExchange && isEqual(targetType, PEOPLE_TYPE.MEMBER);

// ── F 會籍起始日與審閱期檢查 ─────────────────────────────────
// 來源：MembershipCreateContentPlan.jsx handleMembershipDateRangeFeeChange / membershipType useEffect（兩處相同邏輯）
// 一般會籍（NORMAL）時，若「審閱期結束日的當日最後時刻」晚於「會籍起始日的當日最初時刻」，
// 代表會籍起始日早於審閱期結束日 → 視為不合法（invalidMembershipStart）。
// 對應原式：membershipType===NORMAL && isAfter(getLastOfDay(reviewEndDate), getFirstOfDay(startDate), 'YYYY-MM-DDTHH:mm')
export const isMembershipStartBeforeReview = ({ membershipType, startDate, reviewEndDate }) =>
    isEqual(membershipType, MEMBERSHIP_TYPE.NORMAL) &&
    isAfter(getLastOfDay(reviewEndDate), getFirstOfDay(startDate), 'YYYY-MM-DDTHH:mm');

// ── G 入會方案試算（rangeFee）參數組裝與回應對應 ─────────────
// 來源：MembershipCreateContent.jsx doDocMembershipRangeFeeApi

// 依「目前 sourceData」與「本次異動 params」組出後端試算 API 的請求參數。
// sourceData：元件 state（原始資料）；params：本次表單異動內容（含 tabIndex）。
// handlePlansData：由元件端傳入的純函式（原 import 自 MembershipCreateContentPlan），
//   負責在有 plans 時把 plans 帶入 result，避免此檔反向相依元件檔。
// 僅搬移不改行為，對應原式 handleParams(_params)。
export const buildRangeFeeParams = ({ sourceData, params, handlePlansData }) => {
    let temporarySourceData = cloneDeep(sourceData);
    temporarySourceData = mergeDeep(temporarySourceData, params);
    temporarySourceData = handlePlansData(params, temporarySourceData);
    const { contractMain = {} } = temporarySourceData || {};

    return {
        targetType: temporarySourceData.targetType,
        targetID: temporarySourceData.targetID,
        tabIndex: params.tabIndex,
        applyDate: parseDate(temporarySourceData.applyDate, 'YYYY-MM-DDTHH:mm:ss'),
        memberType: contractMain.memberType,
        paymentType: contractMain.paymentType,
        membershipTerm: contractMain.membershipTerm,
        membershipType: contractMain.membershipType,
        plans: contractMain.plans,
        voucherCode: contractMain.voucherCode,
        membershipStartDate: parseDate(contractMain.startDate, 'YYYY-MM-DDTHH:mm:ss'),
        contractVersion: temporarySourceData.contractVersion,
        debitDayOfMonth: temporarySourceData.contractPayment.debitDayOfMonth,
        hasRefill: temporarySourceData.hasRefill,
        monthlyFee: temporarySourceData.contractFee.monthlyFee,
        nextMonthFee: temporarySourceData.contractFee.nextMonthFee,
        firstMonthFee: temporarySourceData.contractFee.firstMonthFee,
    };
};

// 將後端試算 API 回應 resp 對應為要合併回 sourceData 的結構。
// membershipType：由 buildRangeFeeParams 產出的請求 membershipType；有值時覆寫回 contractMain。
// 僅搬移不改行為，對應原式 doDocMembershipRangeFeeApi 內組裝 result 的區塊。
export const mapRangeFeeResponse = (resp, { membershipType } = {}) => {
    const {
        contractStartDate,
        contractEndDate,
        startDate,
        endDate,
        reviewStartDate,
        reviewEndDate,
        monthlyFee,
        joinFee,
        firstMonthFee,
        nextMonthFee,
        debitStartDate,
        debitEndDate,
        debitDayOfMonth,
        debitFirstDate,
        canChangeMembershipTerm,
        canExchange,
        canModifyMembershipTerm,
        contractVersion,
        contractVersionText,
    } = resp;
    const result = {
        canChangeMembershipTerm,
        canExchange,
        canModifyMembershipTerm,
        contractVersion,
        contractVersionText,
        contractMain: {
            contractStartDate,
            contractEndDate,
            startDate,
            endDate,
            reviewStartDate,
            reviewEndDate,
        },
        contractFee: {
            monthlyFee,
            joinFee,
            firstMonthFee,
            nextMonthFee,
        },
        contractPayment: {
            debitStartDate,
            debitEndDate,
            debitDayOfMonth,
            debitFirstDate,
        },
    };
    if (membershipType) result.contractMain.membershipType = membershipType;
    return result;
};
